import { useId } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Switch,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { settingsHref } from '@/features/settings/settingsHref';
import { useMe } from '@/session/meHooks';
import { useAutomationGate } from './useAutomationGate';

const PRESETS = [8000, 9000, 9500, 9900];
// tmm-api reads 0 bp as 'apply every proposal' (§16.5, item 0018): no user action may produce it.
const EVERY_SUGGESTION = 0;

export function AutoApplyThresholdField({
  label,
  value,
  onChange,
  automationKeys,
  disabled = false,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  automationKeys: string[];
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const me = useMe();
  const gate = useAutomationGate();
  const overridden =
    !me.user.aiEnabled ||
    gate.catalog?.groups.some((group) =>
      group.items.some((item) => automationKeys.includes(item.key) && !item.enabled),
    );
  // A stored value outside the presets is shown as its own honest option and sent back only while
  // the person leaves it; once they pick, only a preset can be chosen.
  const options =
    value !== null && !PRESETS.includes(value)
      ? [...PRESETS, value].sort((a, b) => a - b)
      : PRESETS;
  return (
    <FormControl fullWidth disabled={disabled}>
      <FormLabel id={`${id}-group`}>{label}</FormLabel>
      <FormControlLabel
        label={t('autoApply.switch')}
        control={
          <Switch
            checked={value !== null}
            onChange={(_event, checked) => onChange(checked ? 9500 : null)}
            slotProps={{ input: { 'aria-describedby': `${id}-help` } }}
          />
        }
      />
      {value !== null && (
        <FormControl fullWidth disabled={disabled}>
          <InputLabel id={`${id}-label`}>{t('autoApply.confidence')}</InputLabel>
          <Select
            labelId={`${id}-label`}
            label={t('autoApply.confidence')}
            value={value}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (PRESETS.includes(next) || next === value) onChange(next);
            }}
            aria-describedby={`${id}-help`}
          >
            {options.map((bp) => (
              <MenuItem key={bp} value={bp}>
                {bp === EVERY_SUGGESTION
                  ? t('autoApply.everySuggestion')
                  : t('autoApply.percent', { n: bp / 10000 })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormHelperText id={`${id}-help`}>
        {overridden ? (
          <Link component={RouterLink} to={settingsHref('ai-automation')}>
            {t('autoApply.override')}
          </Link>
        ) : (
          t('autoApply.help')
        )}
      </FormHelperText>
    </FormControl>
  );
}
